import {
  createContext,
  FC,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { useChamadaAtiva } from './ChamadaAtivaProvider'
import { SESSAO_INICIAL, SessaoAtendimento, SessaoAtendimentoContextValue } from './core/_models'

type EstadoSessao = (SessaoAtendimento & { atendimentoId: number }) | null

const SessaoAtendimentoContext = createContext<SessaoAtendimentoContextValue | undefined>(undefined)

/**
 * O que o profissional estava fazendo na tela do atendimento (rascunho da anamnese, "salvo", alerta de
 * observações já exibido), separado do contexto da chamada: digitar não re-renderiza o player.
 * Só memória — nada em localStorage/sessionStorage (dado clínico, LGPD).
 */
export const SessaoAtendimentoProvider: FC<{ children: ReactNode }> = ({ children }) => {
  const { atendimentoId: chamadaId } = useChamadaAtiva()
  const [sessao, setSessao] = useState<EstadoSessao>(null)
  const chamadaAnteriorRef = useRef<number | null>(chamadaId)

  // A sessão vive enquanto a chamada existir: encerrada a chamada, é descartada.
  useEffect(() => {
    if (chamadaAnteriorRef.current !== null && chamadaId === null) setSessao(null)
    chamadaAnteriorRef.current = chamadaId
  }, [chamadaId])

  const podeUsarSessao = useCallback(
    (atendimentoId: number) => chamadaId === null || chamadaId === atendimentoId,
    [chamadaId]
  )

  const obterSessao = useCallback(
    (atendimentoId: number) =>
      podeUsarSessao(atendimentoId) && sessao?.atendimentoId === atendimentoId ? sessao : null,
    [podeUsarSessao, sessao]
  )

  const atualizarSessao = useCallback(
    (atendimentoId: number, parcial: Partial<SessaoAtendimento>) => {
      if (!podeUsarSessao(atendimentoId)) return
      setSessao((atual) =>
        atual?.atendimentoId === atendimentoId
          ? { ...atual, ...parcial }
          : { atendimentoId, ...SESSAO_INICIAL, ...parcial }
      )
    },
    [podeUsarSessao]
  )

  const value = useMemo(
    () => ({ podeUsarSessao, obterSessao, atualizarSessao }),
    [podeUsarSessao, obterSessao, atualizarSessao]
  )

  return (
    <SessaoAtendimentoContext.Provider value={value}>{children}</SessaoAtendimentoContext.Provider>
  )
}

/** `undefined` fora do provider — quem consome cai no estado local. */
export const useSessaoAtendimentoContexto = () => useContext(SessaoAtendimentoContext)
